import React from 'react';

interface ValidatorApproval {
  validator: string;
  signature: string;
  timestamp: string;
  status: 'pending' | 'approved' | 'rejected';
}

interface TransactionApprovalsProps {
  approvals: ValidatorApproval[];
  requiredApprovals: number;
}

const TransactionApprovals: React.FC<TransactionApprovalsProps> = ({ 
  approvals, 
  requiredApprovals 
}) => {
  const approvedCount = approvals.filter(a => a.status === 'approved').length;
  const pendingCount = approvals.filter(a => a.status === 'pending').length;
  const rejectedCount = approvals.filter(a => a.status === 'rejected').length;

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-500">
            <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case 'rejected':
        return (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-500">
            <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        );
      case 'pending':
      default:
        return (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-yellow-500">
            <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  const getStatusBadge = (status: string) => {
    const classes = {
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'pending': 'bg-yellow-100 text-yellow-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        classes[status as keyof typeof classes] || 'bg-gray-100 text-gray-800'
      }`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const progressPercentage = Math.round((approvedCount / requiredApprovals) * 100);

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Validator Approvals</h3>
            <p className="text-sm text-gray-500 mt-1">
              Multi-signature validation progress
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm font-medium text-gray-900">
              {approvedCount} of {requiredApprovals} required
            </div>
            <div className="text-xs text-gray-500">
              {progressPercentage}% complete
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-6">
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-600">Approval Progress</span>
            <span className="text-gray-900 font-medium">{approvedCount}/{requiredApprovals}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                approvedCount >= requiredApprovals ? 'bg-green-500' : 'bg-blue-500'
              }`}
              style={{ width: `${Math.min(progressPercentage, 100)}%` }}
            ></div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-900">Approved</p>
                <p className="text-2xl font-bold text-green-900">{approvedCount}</p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-yellow-900">Pending</p>
                <p className="text-2xl font-bold text-yellow-900">{pendingCount}</p>
              </div>
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-900">Rejected</p>
                <p className="text-2xl font-bold text-red-900">{rejectedCount}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Validator List */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-900 uppercase tracking-wide">
            Validator Signatures ({approvals.length})
          </h4>

          {approvals.length === 0 ? (
            <div className="text-center py-8">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 01-3 0m3 0h.75m-9 0h.75m-9 0h.75" />
              </svg>
              <h3 className="mt-4 text-sm font-medium text-gray-900">No validator signatures</h3>
              <p className="mt-2 text-sm text-gray-500">
                Validator signatures will appear here as they approve the transaction.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {approvals.map((approval, index) => (
                <div key={index} className="py-4">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      {getStatusIcon(approval.status)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <p className="text-sm font-medium text-gray-900">
                            Validator #{index + 1}
                          </p>
                          {getStatusBadge(approval.status)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatTimestamp(approval.timestamp)}
                        </div>
                      </div>

                      <div className="mt-2">
                        <div className="text-xs font-medium text-gray-500 mb-1">Validator Address</div>
                        <div className="flex items-center space-x-2">
                          <code className="text-sm text-gray-900 bg-gray-50 px-2 py-1 rounded break-all">
                            {approval.validator}
                          </code>
                          <button
                            onClick={() => copyToClipboard(approval.validator)}
                            className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {approval.signature && (
                        <div className="mt-2">
                          <div className="text-xs font-medium text-gray-500 mb-1">Signature</div>
                          <div className="flex items-center space-x-2">
                            <code className="text-sm text-gray-900 bg-gray-50 px-2 py-1 rounded break-all">
                              {approval.signature.slice(0, 20)}...{approval.signature.slice(-20)}
                            </code>
                            <button
                              onClick={() => copyToClipboard(approval.signature)}
                              className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Validation Status */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className={`rounded-lg p-4 ${
            approvedCount >= requiredApprovals 
              ? 'bg-green-50 border border-green-200' 
              : approvedCount > 0 
              ? 'bg-blue-50 border border-blue-200'
              : 'bg-gray-50 border border-gray-200'
          }`}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {approvedCount >= requiredApprovals ? (
                  <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : approvedCount > 0 ? (
                  <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <h3 className={`text-sm font-medium ${
                  approvedCount >= requiredApprovals 
                    ? 'text-green-900' 
                    : approvedCount > 0 
                    ? 'text-blue-900'
                    : 'text-gray-900'
                }`}>
                  {approvedCount >= requiredApprovals 
                    ? 'Transaction Fully Approved' 
                    : approvedCount > 0 
                    ? 'Partial Approval - Awaiting More Signatures'
                    : 'Awaiting Validator Signatures'
                  }
                </h3>
                <p className={`text-sm ${
                  approvedCount >= requiredApprovals 
                    ? 'text-green-700' 
                    : approvedCount > 0 
                    ? 'text-blue-700'
                    : 'text-gray-700'
                }`}>
                  {approvedCount >= requiredApprovals 
                    ? 'This transaction has received the required number of validator signatures and can proceed.'
                    : `${requiredApprovals - approvedCount} more signature${requiredApprovals - approvedCount === 1 ? '' : 's'} needed to complete validation.`
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionApprovals; 